import React, { FC, useEffect, useState, useMemo, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useIntl } from "react-intl";

import {
  ConfigContext,
  Language,
  PostBillAPIContext,
  PostBillContext,
} from "@app/provider";
import { SpinnerView } from "@app/components";
import { PaymentPlan } from "@app/postBill/models/apiControllerModel";
import { routes } from "@app/routing";
import { logMsg } from "@app/utils/logMsg";
import BillingPaymentForm from "../billingPaymentForm";

const BillingPaymentContainer: FC = () => {
  const { config } = useContext(ConfigContext);
  const {
    createPayment,
    createPaymentPlan,
    createPayFullBalance,
    getCTSToken,
  } = useContext(PostBillAPIContext);
  const { paidDirectPayments, paidPaymentPlans } = useContext(PostBillContext);
  const { totalAmount, selectedBills, selectedPaymentPlan } =
    useContext(PostBillContext);
  const { locale } = useIntl();
  const [clientSecret, setClientSecret] = useState<string>("");
  const [customerSessionClientSecret, setCustomerSessionClientSecret] =
    useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentPlanId, payFullBalance } = location.state ?? {};
  const ctsToken = getCTSToken?.();
  const currentLocale = locale === "es" ? Language.ES : Language.EN;

  const publishableKey = config.preBill.postBillStripeKey;
  const stripePromise = useMemo(
    () => loadStripe(publishableKey, { locale: currentLocale }),
    [currentLocale]
  );

  const handleCreatePayment = async () => {
    const anyItemPaid = selectedBills.some((bill) =>
      paidDirectPayments.has(bill?.lineItemUid)
    );
    if (anyItemPaid) {
      setLoading(false);
      return;
    }
    try {
      const response = await createPayment({
        currency: selectedBills[0]?.currency.toLocaleLowerCase(),
        amount: Math.round(totalAmount * 100) / 100,
        itemList: selectedBills.map((item) => {
          return {
            bosOrderUid: item.orderUid,
            bosOrderLineItemUid: item.lineItemUid,
          };
        }),
      });
      setClientSecret(response.data?.clientSecret as string);
      setCustomerSessionClientSecret(
        response.data?.customerSessionSecret as string
      );
    } catch {
      logMsg("error", "postbill.payment", "Error on handleCreatePayment");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePaymentPlan = async () => {
    const anyItemPaid = selectedBills.some((bill) =>
      paidPaymentPlans.has(bill?.lineItemUid)
    );
    if (anyItemPaid) {
      setLoading(false);
      return;
    }
    try {
      const response = await createPaymentPlan({
        currency: selectedBills[0]?.currency.toLocaleLowerCase(),
        amount: Math.round(totalAmount * 100) / 100,
        itemList: selectedBills.map((item) => {
          return {
            bosOrderUid: item.orderUid,
            bosOrderLineItemUid: item.lineItemUid,
          };
        }),
        numberOfInstallment: selectedPaymentPlan,
      });
      setClientSecret(response.data?.clientSecret as string);
      setCustomerSessionClientSecret(
        response.data?.customerSessionSecret as string
      );
    } catch {
      logMsg("error", "postbill.payment", "Error on handleCreatePaymentPlan");
    } finally {
      setLoading(false);
    }
  };

  const handlePayFullBalanceButton = async () => {
    try {
      const response = await createPayFullBalance(paymentPlanId);

      setClientSecret(response.data?.clientSecret as string);
      setCustomerSessionClientSecret(
        response.data?.customerSessionSecret as string
      );
    } catch {
      logMsg(
        "error",
        "postbill.payment",
        "Error on handlePayFullBalanceButton"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ctsToken) {
      navigate(routes.reviewBills, {
        state: { from: routes.billingPayment },
      });
    }

    if (payFullBalance) handlePayFullBalanceButton();
    else if (selectedPaymentPlan === PaymentPlan.None) handleCreatePayment();
    else handleCreatePaymentPlan();
  }, []);

  const options = {
    layout: {
      type: "accordion",
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: false,
    },
    clientSecret: clientSecret,
    customerSessionClientSecret: customerSessionClientSecret,
  };

  const elementsOptions = useMemo(
    () => options,
    [clientSecret, customerSessionClientSecret]
  );

  return (
    <>
      <SpinnerView isLoading={loading} />
      {clientSecret && (
        <Elements
          stripe={stripePromise}
          options={elementsOptions}
          key={currentLocale}
        >
          {paymentPlanId ? (
            <BillingPaymentForm paymentPlanId={paymentPlanId} />
          ) : (
            <BillingPaymentForm />
          )}
        </Elements>
      )}
    </>
  );
};

export default BillingPaymentContainer;
